import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { withSize } from 'react-sizeme';
import { makeStyles } from '@material-ui/core/styles';
import { Colors, Metrics } from '../../themes';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import SvgIcon from '../../icons/SvgIcon';
import Table from '../common/Table';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import '../ajaxElements/AjaxComponents.css';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`wrapped-tabpanel-${index}`}
      aria-labelledby={`wrapped-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `wrapped-tab-${index}`,
    'aria-controls': `wrapped-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme) => {
  return {
    DetailsWidget: {
      display: 'flex',
      flexDirection: 'column',
      overflowWrap: 'break-word',
    },
    headerButtonsContainer: {
      background: Colors.navBackgroundGrey,
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      borderRadius: '4px 4px 0 0',
      padding: 10,
      [theme.breakpoints.down(Metrics.minWidth)]: {
        padding: 6,
        justifyContent: 'flex-start',
      },
    },
    button: {
      margin: theme.spacing(2),
      fontSize: 14,
      color: Colors.white,
      border: `1px solid ${Colors.disabledGrey}`,
      '&:hover': {
        background: Colors.disabledGrey,
      },
      '&:disabled': {
        border: `1px solid ${Colors.disabledGrey}`,
        background: Colors.navBackgroundGrey,
        '&:hover': {
          background: Colors.black,
        },
      },
    },
    smallSizeButton: {
      height: 35,
      fontSize: 12,
      paddingLeft: 6,
      paddingRight: 6,
    },
    saveButton: {
      background: Colors.blue,
      border: 'none',
      '&:hover': {
        background: Colors.lightBlue,
      },
    },
    deleteButton: {
      margin: 10,
      minWidth: 38,
      width: 38,
      maxWidth: 38,
      padding: 0,
      borderColor: Colors.red,
      '&:hover': {
        background: Colors.red,
      },
      [theme.breakpoints.down(Metrics.minWidth)]: {
        marginRight: 8,
      },
      '&:disabled:hover': {
        background: Colors.black,
      },
    },
    // details
    detailsContainer: {
      padding: '25px 20px',
      [theme.breakpoints.down('xs')]: {
        padding: 15,
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    formContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    switch: {
      marginTop: 34,
      marginLeft: 0,
      color: Colors.lightGrey,
    },
    readOnly: {
      color: Colors.red,
      textTransform: 'none',
      border: '2px solid',
      padding: '0 4px',
      fontSize: 15,
      marginLeft: 10,
    },
    tabs: {
      marginTop: 30,
    },
    openModalButton: {
      cursor: 'pointer',
      background: 'transparent',
    },
    textField2: {
      cursor: 'pointer',
    },
    onlineStatus: {
      marginTop: 16,
    },
  };
});

const ControllerDetails = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { width } = props.size;
  const isSmall = width < Metrics.formDetails.small;
  const formInputWidth = isSmall ? width - 40 : (width - 60) / 2;
  const {
    controllerId,
    deleteButtonText,
    add1ButtonText,
    onDeleteButtonClick,
    onAdd1ButtonClick,
    onSaveButtonClick,
    isDeleteButtonDisabled,
    onChange,
    hideDetails,
    controllerName,
    isDeleted,
    isUntouched,
    setUntouched,
    saveButtonDisabled,
    openChangeCustomerModal,
    openChangeSiteModal,
    isOnline,
  } = props;
  const smallerThanMedium = useMediaQuery(`(max-width: ${Metrics.breakpoints.md}px)`);
  const handleSubmit = (event) => {
    event.preventDefault();
    onSaveButtonClick();
  };
  // controllerName
  const [isControllerNameError, setControllerNameError] = useState(false);
  const [controllerNameHelperText, setControllerNameHelperText] = useState(null);
  const [isControllerNameTouched, setControllerNameTouched] = useState(false);

  const [isFormValid, setFormValid] = useState(false);

  const setFieldsTouched = useCallback(
    (value) => {
      setControllerNameTouched(value);
      setUntouched(false);
    },
    [setUntouched],
  );

  useEffect(() => {
    const validate = () => {
      const isControllerNameValid = controllerName !== '';
      if (!isControllerNameValid && !isControllerNameTouched) {
        setFormValid(false);
      }
      if (!isControllerNameValid && isControllerNameTouched) {
        setControllerNameError(true);
        setControllerNameHelperText(t('validation.requiredField'));
        setFormValid(false);
      }
      if (isControllerNameValid) {
        setControllerNameError(false);
        setControllerNameHelperText(null);
      }
      if (isControllerNameValid) {
        setFormValid(true);
      }
    };
    validate();
  }, [controllerName, isControllerNameTouched, t]);
  useEffect(() => {
    if (isUntouched) {
      setFieldsTouched(false);
      setControllerNameError(false);
      setControllerNameHelperText(null);
    }
  }, [isUntouched, setFieldsTouched]);
  const handleBlur = (event) => {
    const { name } = event.target;
    if (name === 'controllerName') {
      setControllerNameTouched(true);
    }
    if (name === 'controllerName') {
      props.setUntouched(false);
    }
  };
  //Tabs
  const [tabValue, setTabValue] = useState('status');
  useEffect(() => setTabValue('status'), [props.reportModelId]);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <div className={classes.DetailsWidget}>
      <form id="detailsForm" noValidate autoComplete="off" onSubmit={handleSubmit}>
        <div className={classes.headerButtonsContainer}>
          <Button
            type="button"
            className={clsx(classes.deleteButton, isSmall && classes.smallSizeButton)}
            variant="outlined"
            aria-label={deleteButtonText}
            onClick={onDeleteButtonClick}
            disabled={isDeleteButtonDisabled}
          >
            <SvgIcon
              icon={'Trash'}
              color={isDeleteButtonDisabled ? Colors.disabledGrey : Colors.white}
              stroke={2}
              width={14}
            />
          </Button>
          {controllerId && typeof isOnline !== 'undefined' && (
            <div className={classes.onlineStatus}>
              <Typography variant="body1">
                {isOnline ? 'online' : 'offline'}
                <span className={`space-left status_dot ${isOnline ? 'button_on' : 'button_off'}`}></span>
              </Typography>
            </div>
          )}
          {onAdd1ButtonClick && (
            <Button
              type="button"
              variant="outlined"
              className={clsx(classes.button, isSmall && classes.smallSizeButton)}
              onClick={onAdd1ButtonClick}
              endIcon={<SvgIcon icon="Add" width={smallerThanMedium ? 16 : 20} color={Colors.white} />}
            >
              {add1ButtonText}{' '}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            className={clsx(classes.button, classes.saveButton, isSmall && classes.smallSizeButton)}
            endIcon={
              <SvgIcon
                icon="Save"
                width={smallerThanMedium ? 16 : 20}
                color={!isFormValid || saveButtonDisabled ? Colors.disabledGrey : Colors.white}
              />
            }
            disabled={!isFormValid || saveButtonDisabled}
            onMouseEnter={() => setFieldsTouched(true)}
          >
            {t('Global_save')}
          </Button>
        </div>
        {!hideDetails && (
          <div className="bodyComponent">
            <div className={classes.detailsContainer}>
              <Typography variant="h2">
                {t('controllerManager_tab1_header').toUpperCase()}
                {isDeleted && <span className={classes.readOnly}>Deleted</span>}
              </Typography>

              <div className={classes.formContainer}>
                <TextField
                  style={{ width: formInputWidth }}
                  id="controllerUid"
                  label={t('controllerManager_searchControllers_col1')}
                  name="controllerUid"
                  margin="normal"
                  value={props.controllerUid}
                  onChange={onChange}
                  onBlur={handleBlur}
                  disabled
                />
                <TextField
                  style={{ width: formInputWidth }}
                  id="controllerName"
                  label={t('controllerManager_searchControllers_col2')}
                  name="controllerName"
                  margin="normal"
                  value={props.controllerName}
                  onChange={onChange}
                  required
                  onBlur={handleBlur}
                  error={isControllerNameError}
                  helperText={controllerNameHelperText}
                />
                <div className={classes.openModalButton} onClick={openChangeCustomerModal}>
                  <TextField
                    style={{ width: formInputWidth, cursor: 'pointer' }}
                    id="customerName"
                    className={classes.textField2}
                    label={t('controllerManager_searchControllers_col3')}
                    name="customerName"
                    margin="normal"
                    value={props.customerName || ''}
                    onChange={onChange}
                    disabled
                  />
                </div>
                <div className={classes.openModalButton} onClick={openChangeSiteModal}>
                  <TextField
                    style={{ width: formInputWidth }}
                    id="siteName"
                    className={classes.textField}
                    label={t('controllerManager_searchControllers_col4')}
                    name="siteName"
                    margin="normal"
                    value={props.siteName || ''}
                    onChange={onChange}
                    disabled
                  />
                </div>
                <div className={classes.tabs}>
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="filter tabs">
                    <Tab
                      classes={{ root: classes.tab }}
                      value="status"
                      label={t('controllerManager_controllerInfo_status')}
                      wrapped
                      {...a11yProps('status')}
                    />
                    <Tab
                      classes={{ root: classes.tab }}
                      value="settings"
                      label={t('controllerManager_controllerInfo_settings')}
                      {...a11yProps('settings')}
                    />
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
      {!hideDetails && (
        <div className={classes.tabsContainer}>
          {/* status panel */}
          <TabPanel className={classes.tabPanel} value={tabValue} index="status">
            <Table
              data={props.controllerStatus}
              columns={[
                { title: t('controllerManager_controllerEvents_col1'), field: 'property' },
                { title: t('controllerManager_controllerEvents_col2'), field: 'value' },
              ]}
              noDataText={t('controllers.noStatus')}
              noHeader
              noFooter
              {...props}
            />
          </TabPanel>
          {/* settings panel */}
          <TabPanel className={classes.tabPanel} value={tabValue} index="settings">
            <Table
              data={props.controllerSettings}
              columns={[
                { title: t('controllerManager_controllerEvents_col1'), field: 'property' },
                { title: t('controllerManager_controllerEvents_col2'), field: 'value' },
              ]}
              noDataText={t('controllers.noSettings')}
              noHeader
              noFooter
              handleClick={props.handleTabClick}
              {...props}
            />
          </TabPanel>
        </div>
      )}
    </div>
  );
};

export default withSize()(ControllerDetails);
